<script lang="ts">
    import { getContext, createEventDispatcher } from 'svelte';
    import {overlay} from "../Store";
    import type {ChatData, ChatMessageData, ContactData, UserData} from '../Types'
    import {getRouteById} from "../Nav";
    import {authFetch} from "../Auth";
    import throttle from "@jcoreio/async-throttle";
    import {DateTime} from "luxon";
    import Message from "./Message.svelte";
    import ContactButton from "./ContactButton.svelte";
    import Sort from "./Sort.svelte";
    import {params} from "../Page/UserContacts.svelte";
    import ExportDialog from "./ExportDialog.svelte";

    const dispatch = createEventDispatcher();
    const { open } = getContext('simple-modal');

    // Component attributes
    export let user: UserData
    export let chat_id: string
    let chat: ChatData

    let filter = {
        chat_id: null,
        telegram_user_id: null,
        offset: 0,
        limit: 100,
        phrase: "",
        order: "DESC",
        orderBy: "date",
    }
    let listPromise: Promise<ChatMessageData>
    let list: ChatMessageData[] = []
    let count: number = 0
    let secretChatMessageText = null
    let secretChatFiles;
    let secretChatImage;
    let secretChatFilesFormatted = {};
    let allowedFilesParams = {
        'image/png': 1024 * 1024,
        'image/jpeg': 1024 * 1024
    }


    $: {
        filter.telegram_user_id = user.telegram_id
        filter.chat_id = chat_id
        update()
    }

    async function loadList() {
        let response = await authFetch('/user/chat/messages', {
            method: 'POST',
            body: JSON.stringify(filter)
        })
        if (response.ok) {
            let data = await response.json()
            list = data.list
            count = data.count
            chat = data.chat
        } else {
            alert(response.statusText)
        }
    }

    let onInput = throttle(() => {
        update()
    }, 1000)

    function onSort(ev: Event){
        if(ev.detail.order === null){
            filter.order = "DESC"
            filter.orderBy = "date"
        } else {
            filter.order = ev.detail.order
            filter.orderBy = ev.detail.orderBy
        }
        update()
    }

    function update() {
        listPromise = loadList()
    }

    function onOpenExportModal() {
        open(
            ExportDialog,
            {
                message: "Параметри експорту",
                onOkay: exportChats
            },
            {
                closeButton: true,
                closeOnEsc: true,
                closeOnOuterClick: true,
            }
        );
    }

    async function exportChats(fromDate, toDate) {
        overlay.set('Downloading')
        let response = await authFetch(`export/chat`, {
            method: 'POST',
            body: JSON.stringify({
                'chatId': chat_id,
                'fromDate': fromDate,
                'toDate': toDate
            }),})
        let filename = 'export-contacts.html'

        if (response.ok) {
            let header = response.headers.get('Content-Disposition');
            let parts = header!.split(';');
            if (parts.length > 0) {
                filename = parts[1].split('=')[1];
                filename = filename.replace(/(^"|"$)/g, '').trim();
            }

            let blob = await response.blob();
            let a = document.createElement('a');
            document.body.appendChild(a);

            let url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = filename;
            a.click();

            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                overlay.set(null)
            }, 10)
        } else {
            alert(response.statusText)
        }
    }
    async function onRepeatParse(chat) {
        let response = await authFetch('user/repeatParse', {
            method: 'POST',
            body: JSON.stringify({'id': user.id, 'requiredTelegramChatId': chat.telegram_chat_id}),
        })
        if (response.ok) {
            let data = await response.json()
            let message: string|null = data.message ?? "Не отримали повідомлення від сервера" ;
            alert(message)
        } else {
            let body:string = await response.text()
            alert(body)
        }
    }
    async function secretChatSendMessage() {
        if (!secretChatMessageText || secretChatMessageText.trim().length < 1) {
            alert('Пусте повідомлення не дозволено відправляти')
            return
        }

        let response = await authFetch('secret-chat/send-message', {
            method: 'POST',
            body: JSON.stringify({
                user_id: user.id,
                telegram_user_id: user.telegram_id,
                telegram_contact_id: chat.telegram_contact_id,
                messageText: secretChatMessageText,
                files: secretChatFilesFormatted
            }),
        })
        if (response.ok) {
            let data = await response.json()
            let message = data.message ?? 'Відповідь без повідомлення'
            secretChatMessageText = null;
            alert(message)
        } else {
            let data = await response.json()
            let errorMessage = response.statusText;
            if ('message' in data && data['message'].length > 0) {
                errorMessage = data['message'];
            }
            alert(errorMessage)
        }
    }

    function onSecretChatFilesChanged() {
        secretChatFilesFormatted = {};
        if (secretChatFiles.length > 1) {
            secretChatFiles = null
            alert('Заборонено відправляти більше 1 файлів')
            return
        }
        let errorMessage = [];
        let resizedWidth = 200;
         for (let secretChatFile of secretChatFiles) {
             if (!(secretChatFile.type in allowedFilesParams)) {
                 errorMessage.push(`${secretChatFile.name} - не дозволений формат файлу`);
                 continue;
             }
             if (secretChatFile.size  > allowedFilesParams[secretChatFile.type]) {
                 errorMessage.push(`${secretChatFile.name} - більше 1Mb`);
                 continue;
             }
             if (secretChatFile.name in secretChatFilesFormatted) {
                continue;
             }
             secretChatFilesFormatted[secretChatFile.name] = {
                 file: null,
                 src: null
             }
             let reader = new FileReader();
             reader.addEventListener("load", () => {
                 let image = new Image();
                 image.src = reader.result;
                 image.onload = function() {
                     let elem = document.createElement('canvas'); //create a canvas
                     let scaleFactor = resizedWidth / image.width;
                     let resizedHeight = Math.round(image.height * scaleFactor);
                     elem.width = resizedWidth;
                     elem.height = resizedHeight;

                     //draw in canvas
                     let ctx = elem.getContext('2d');
                     ctx.drawImage(image, 0, 0, elem.width, elem.height);

                     secretChatFilesFormatted[secretChatFile.name] = {
                         src: reader.result,
                         srcThumb: ctx.canvas.toDataURL(secretChatFile.type, 1),
                         thumbWidth: resizedWidth,
                         thumbHeight: resizedHeight,
                         name: secretChatFile.name,
                         size: secretChatFile.size,
                         type: secretChatFile.type,
                         width: image.width,
                         height: image.height
                     }
                 };
             });
             reader.readAsDataURL(secretChatFile);
         }
         if (errorMessage.length > 0) {
             alert(errorMessage.join("\n"));
         }
    }
</script>
<style>
    .chat-state {
        font-size: 13px;
        margin-bottom: 0px;
    }
    .new-message {
        height:55px;
        min-height:55px;
        max-height:55px;
    }
</style>
<table class="table table-bordered table-sm table-hover">
    <thead class="position-sticky bg-white shadow" style="top: 0px; z-index: 1030;">
    <tr style="border: 0px;">
        <th colspan="100" class="p-1">
            {#await listPromise}{''}{:then _}
            <div class="text-center p-1">
                {chat.title}
                <p class="chat-state">
                    {#if chat.chat_state == 'secretChatStatePending'}
                        Секретний чат ще не створено. Чекаємо, поки інший користувач підключиться до мережі
                    {:else if chat.chat_state == 'secretChatStateClosed'}
                        Секретний чат закритий
                    {:else if chat.chat_state == 'secretChatStateReady'}
                        Секретний чат готовий до використання
                    {/if}
                </p>
                <button class="btn btn-sm btn-primary float-end"><i class="bi bi-arrow-repeat" on:click={onRepeatParse(chat)}></i></button>
                <button class="btn btn-sm btn-success float-end mr-1" on:click={()=>{onOpenExportModal()}}><i class="bi bi-download"></i></button>
                <button class="btn btn-sm btn-primary float-end mr-1"><i class="bi bi-printer" on:click={()=>dispatch('printMode')}></i></button>
            </div>
            {/await}
            <div class="input-group mb-2">
                <input class="form-control" type="search" placeholder="Пошук по тексту повідомлення"
                       bind:value={filter.phrase} on:keyup={onInput} on:change={onInput}>
                <button class="btn btn-info"
                        class:disabled={filter.offset===0}
                        on:click={()=>{filter.offset-=filter.limit;update()}}>
                    <i class="bi bi-chevron-left"></i>
                </button>
                <input class="form-control flex-grow-0" type="number" style="width: 5em;"
                       min="1"
                       max={Math.floor(count / filter.limit)+1}
                       step="1"
                       value={(filter.offset / filter.limit)+1}
                       on:change={(ev)=>{filter.offset=(ev.target.value - 1) * filter.limit; update()}}
                >
                <button class="btn btn-info disabled">
                    / {Math.floor(count / filter.limit)+1}
                </button>
                <button class="btn btn-info"
                        class:disabled={list.length < filter.limit}
                        on:click={()=>{filter.offset+=filter.limit;update()}}>
                    <i class="bi bi-chevron-right"></i>
                </button>
            </div>
            {#await listPromise}{''}{:then _}
                {#if chat.type == 'chatTypeSecret' && chat.chat_state == 'secretChatStateReady'}
                    <div class="input-group">
                        <textarea class="form-control new-message" aria-label="With textarea" bind:value="{secretChatMessageText}"></textarea>
                        <div class="m-lg-2">
                            <button class="btn btn-success" type="button" on:click={() => {secretChatSendMessage()}}>Відправити</button>
                        </div>
                    </div>
                    <div class="mt-1 mb-1">
                        <input class="form-control form-control-sm" type="file" accept=".jpg,.jpeg,.png,.gif" bind:files="{secretChatFiles}" on:change={onSecretChatFilesChanged} >
                    </div>
                    {#if secretChatFilesFormatted}
                        {#each Object.entries(secretChatFilesFormatted) as [index, file]}
                            <img class="mt-1 mr-1" src="{file.srcThumb}" width="{file.thumbWidth}" height="{file.thumbHeight}" alt="{index}"  title="{index}" />
                        {/each}
                    {/if}
                {/if}
            {/await}
        </th>
    </tr>
    <tr style="border: 0px; font-size: 14px;" class="text-ncontactowrap">
        <th>ID</th>
        <th style="min-width: 160px; width: 160px;">
            Дата <Sort orderBy="date" state={filter} orders={["ASC","DESC"]} on:sort={onSort}></Sort>
        </th>
        <th style="width: 100%">Контент</th>
        <th>Автор</th>
    </tr>
    </thead>
    <tbody>
    {#await listPromise}
        <tr>
            <td colspan="1000" class="text-center">
                <div class="spinner-grow"></div>
            </td>
        </tr>
    {:then _}
        {#if list.length > 0}
            {#each list as message}
                {@const contentType = message.content["@type"]}
                <tr>
                    <td rowspan="2">{message.id}</td>
                    <td colspan="3">
                        {message.date ? DateTime.fromISO(message.date).toFormat("yyyy-MM-dd HH:mm:ss") : ''}
                        {#if message?.secret_message?.receiver_read_at != null}
                            <span class="text-primary fs-5" title="{DateTime.fromISO( message?.secret_message?.receiver_read_at).toFormat('yyyy-MM-dd HH:mm:ss')}"><i class="bi bi-check-all"></i></span>
                        {/if}
                        {#if message?.secret_message?.receiver_deleted_at != null}
                            <span class="text-danger fs-6" title="{DateTime.fromISO( message?.secret_message?.receiver_deleted_at).toFormat('yyyy-MM-dd HH:mm:ss')}"><i class="bi bi-trash"></i></span>
                        {/if}
                        <span class="float-end">
                            {#if message.telegram_sender_id === user.telegram_id}
                                <i class="bi bi-chevron-double-left"></i>
                            <!--{:else if message.sender}-->
                            <!--    <ContactButton-->
                            <!--        telegram_id={message.sender.telegram_contact_id}-->
                            <!--        phone={message.sender.phone}-->
                            <!--    >{`${message.sender.first_name} ${message.sender.last_name} ${message.sender.username}`}</ContactButton>-->
                            {:else}
                                {message.telegram_sender_id ? 'tg: ' + message.telegram_sender_id: ''}
                            {/if}
                        </span>
                    </td>
                </tr>
                {#await listPromise}{''}{:then _}
                    <tr>
                        <td colspan="3" style="overflow-wrap: anywhere; text-wrap: normal">
                            {#if message.forward_origin_date != null && message.forward_origin_type != null}
                                <div class="card">
                                    <div class="card-header">
                                        Переслано
                                        {#if message.forward_origin_type == 'messageForwardOriginChannel'}
                                            з каналу: {message.forward_info?.origin?.chat_id ?? '-'}
                                        {:else if message.forward_origin_type == 'messageForwardOriginUser'}
                                            від користувача: {message.forward_info?.origin?.sender_user_id ?? '-'}
                                        {:else if message.forward_origin_type == 'messageForwardOriginHiddenUser'}
                                            від прихованого користувача: {message.forward_info?.origin?.sender_name ?? '-'}
                                        {/if}, дата: {message.forward_origin_date? DateTime.fromISO(message.forward_origin_date).toFormat("yyyy-MM-dd HH:mm:ss") : ''}
                                    </div>
                                    <div class="card-body">
                                        <Message entity={message.content} isChannel="{chat.is_channel}"
                                                 telegramMessageId="{message.telegram_message_id}"
                                                 attachments={message.attachments}/>

                                    </div>
                                    {#if contentType === 'messageVoiceNote' && message.recognized_text && message.recognized_text.length > 0}
                                        <div class="card card-body">{message.recognized_text}</div>
                                    {/if}
                                </div>
                            {:else }
                                <Message entity={message.content} isChannel="{chat.is_channel}"
                                         telegramMessageId="{message.telegram_message_id}"
                                         attachments={message.attachments}/>
                                {#if contentType === 'messageVoiceNote' && message.recognized_text && message.recognized_text.length > 0}
                                    <div class="card card-body">{message.recognized_text}</div>
                                {/if}
                            {/if}
                        </td>
                    </tr>
                {/await}
            {/each}
        {:else}
            <tr>
                <td colSpan="1000" class="text-center">
                    <div class="alert alert-secondary">Немає повідомлень</div>
                </td>
            </tr>
        {/if}
    {:catch error}
        <tr>
            <td colSpan="1000" class="text-center">
                <div class="alert alert-danger">{error}</div>
            </td>
        </tr>
    {/await}
    </tbody>
</table>

